<template lang="pug">
  .main-container.min-view-height.column.middle.center
    h1.title-medium.m-bottom-2 {{ $t('login.signIn') }}
    .loader.row.middle.center(v-if='loading')
      loader(size='inner')
    form.login-container.width-100(@submit.prevent='loginUser' novalidate v-else)
      .column(v-if='!corporativeLogin')
        .m-bottom-1
          label.text-normal.bold {{ $t('login.email') }}
          input.input-border.text-normal.dark-gray(
            type='mail'
            v-model.trim='email'
            :placeholder='$t("login.emailPlaceholder")'
            :class='{ error: invalidEmail || missingEmail || showUserError }'
          )
          .row.m-top-05(v-show='invalidEmail || missingEmail')
            simple-svg.red-icon.m-right-1(:filepath='stopIcon' :width='"16px"' :height='"16px"')
            span.error-desc(v-show='invalidEmail') {{ $t('login.errors.incorrectEmail') }}
            span.error-desc(v-show='missingEmail') {{ $t('login.errors.missingEmail') }}
        .m-bottom-1
          hidden-form-input(
            :label='$t("login.password")'
            :placeholder='$t("login.passwordPlaceholder")'
            v-model='password'
            :inputError='missingPassword || showUserError'
          )
          .row.m-top-05(v-if='missingPassword')
            simple-svg.red-icon.m-right-1(:filepath='stopIcon' :width='"16px"' :height='"16px"')
            span.error-desc {{ $t('login.errors.missingPassword') }}
      .column.m-top-2(v-else)
        .m-bottom-1
          label.text-normal.bold {{ $t("login.companyId") }}
          input.input-border.text-normal.dark-gray(
            type='text'
            v-model='subdomain'
            :placeholder='$t("login.companyId")'
            :class='{ error: invalidSubdomain }'
          )
      .errors-container.row.m-bottom-2(v-if='showUserError && !corporativeLogin')
        simple-svg.red-icon.m-right-1(:filepath='stopIcon' :width='"16px"' :height='"16px"')
        span.error-desc {{ $t('login.errors.userNotFound') }}
      .column.m-top-1.m-bottom-2(v-if='!corporativeLogin')
        button.blue.text-normal.text-left(@click='goToForgotPassword' type='button') {{ $t('login.forgotPassword.title') }}
        button.m-top-1.blue.text-normal.text-left(type='button' @click='() => setCorporativeLogin(true)') {{ $t('login.corporativeAccess') }}
      .column.m-bottom-3.m-top-1(v-if='!corporativeLogin')
        button.m-bottom-1.primary-button(type='submit') {{ $t('login.login') }}
        span.text-normal.self-center
          | {{ $t('login.areYouNew') }}
          router-link.text-normal.blue.m-left-05(@click.native='closeLoginModal' :to='{name: routes.trial}') {{ $t('login.register') }}
      .row.middle.m-bottom-2.m-top-1(v-else)
        button.item-1.m-right-05.secondary-button.text-normal.text-left(@click='() => setCorporativeLogin(false)' type='button') {{ $t('login.back') }}
        button.item-1.m-left-05.primary-button(type='button' @click='goToCorporativeLogin') {{ $t('login.login') }}
      .column(v-if='!corporativeLogin')
        .divider.relative.width-100.m-bottom-3
          span.absolute.text-normal.o O
        a.secondary-button.row.m-bottom-1(type='button' :href='googleLink')
          simple-svg.m-right-1(:filepath='googleGIcon' width='18px' height='18px')
          | {{ $t('login.loginWithGoogle') }}
        button.secondary-button.row(type='button' @click='signInWithMicrosoft')
          simple-svg.m-right-1(:filepath='microsoftIcon' width='18px' height='18px')
          | {{ $t('login.loginWithMicrosoft') }}
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex'
import { required, email } from 'vuelidate/lib/validators'
import amplitude from 'amplitude-js'
import HiddenFormInput from '@/components/HiddenFormInput.vue'
import stopIcon from '@/assets/ic_stop.svg'
import { routes } from '@/router'
import { emailRegex } from '@/utils/validators'
import Loader from '@/components/Loader'
import googleGIcon from '@/assets/ic_google_g.svg'
import microsoftIcon from '@/assets/ic_microsoft.svg'
import { MSAL } from '@/config/msalConfig'
import { getGoogleAccessToken } from '../utils/url'

export default {
  components: { HiddenFormInput, Loader },
  data() {
    return {
      stopIcon,
      microsoftIcon,
      googleGIcon,
      routes,
      passwordInputType: 'password',
      email: '',
      password: '',
      subdomain: '',
      privatePath: null,
      showErrors: false,
      corporativeLogin: false,
      loading: false
    }
  },
  async created() {
    if (this.redirectPath) {
      this.privatePath = this.redirectPath
      this.setRedirectPath(null)
    }
    await MSAL.initialize()
    window.sessionStorage.clear()
    if (this.$route.hash) this.signInWithGoogle()
  },
  validations() {
    return {
      password: { required },
      email: { required, email, emailRegex },
      subdomain: this.corporativeLogin ? { required } : {}
    }
  },
  computed: {
    ...mapState({
      showUserError: state => state.userInfo.showInvalidUserError,
      googleUserInfo: state => state.userInfo.googleUserInfo,
      redirectPath: state => state.behavior.redirectPath
    }),
    invalidEmail() {
      return this.showErrors && !this.$v.email.emailRegex
    },
    missingEmail() {
      return this.showErrors && !this.$v.email.required
    },
    missingPassword() {
      return this.showErrors && !this.$v.password.required
    },
    invalidSubdomain() {
      return this.showErrors && !this.$v.subdomain.required
    },
    googleLink() {
      const clientId = process.env.VUE_APP_GOOGLE_CLIENT_ID
      const redirectUri = `${window.location.origin}/login`
      const responseType = 'token'
      const scope = 'email profile'
      return `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=${responseType}&scope=${scope}`
    }
  },
  methods: {
    ...mapMutations([ 'setRedirectPath' ]),
    ...mapActions([
      'showElement',
      'hideElement',
      'login',
      'setUserError',
      'getLoggedUserInfo',
      'externalGoogleLogin',
      'externalMicrosoftLogin'
    ]),
    async loginUser() {
      if (this.$v.$invalid) {
        this.showErrors = true
      } else {
        const { email, password } = this
        if (email && password) {
          amplitude.getInstance().logEvent('Login')
          this.showErrors = false
          await this.login({ email, password })
          if (!this.showUserError) {
            await this.getLoggedUserInfo()
            this.hideElement({ element: 'fullPageLoader' })
            this.privatePath ? this.$router.push({ path: this.privatePath }) : this.$router.push({ name: routes.feed })
            this.setRedirectPath(null)
          }
        }
      }
    },
    async signInWithGoogle() {
      this.loading = true
      const token = getGoogleAccessToken(this.$route)
      const success = await this.externalGoogleLogin(token)
      if (success) {
        await this.getLoggedUserInfo()
        this.privatePath ? this.$router.push({ path: this.privatePath }) : this.$router.push({ name: routes.feed })
        this.setRedirectPath(null)
      }
    },
    signInWithMicrosoft() {
      MSAL.loginPopup({ scopes: ['User.Read'] })
        .then(async response => {
          if (response) {
            this.loading = true
            const success = await this.externalMicrosoftLogin(response.accessToken)
            if (success) {
              await this.getLoggedUserInfo()
              this.privatePath ? this.$router.push({ path: this.privatePath }) : this.$router.push({ name: routes.feed })
              this.setRedirectPath(null)
            }
            this.loading = false
          }
        })
        .catch((error) => {
          console.error(error)
        })
    },
    goToForgotPassword() {
      this.showElement({ element: 'forgotPasswordModal' })
    },
    setCorporativeLogin(status) {
      this.showErrors = false
      this.showUserError = false
      this.corporativeLogin = status
    },
    goToCorporativeLogin() {
      const baseUrl = window.location.host
      const protocol = window.location.protocol
      window.location.replace(`${protocol}//${this.subdomain}.${baseUrl.replace('www.', '')}`)
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'src/scss/variables/colors';
@import 'src/scss/variables/sizes';

.loader{
  min-height: 450px;
}

.login-container{
  max-width: 480px;
  margin: 0 auto;
}

.divider{
  border-bottom: 1px solid $gray;
  .o {
    right: 50%;
    background: $white;
    border: 6px solid $white;
    transform: translate(50%, -50%);
  }
}

.errors-container{
  flex: 1;
  width: 100%;
}

.secondary-button, .primary-button{
  max-width: none;
}

.input-border:focus{
  border-color: $blue;
}
</style>
